import type { MonitoringCellData } from '@lmc/ts-common'
import { Text } from '@lmc/ui-kit/src/general'
import useFeatureFlag from 'hooks/useFeatureFlags'
import { NAMESPACE } from 'locale'
import { monitoringReportTranslations as translations } from 'locale/monitoringReports'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  formatData,
  popoverReportMappings
} from '../../../utils/monitoringReportsUtils'

type ReportingId = keyof typeof popoverReportMappings

type MonitoringReportCellPopoverProps = {
  cellData: MonitoringCellData | undefined
  reportingId: string
}
const MonitoringReportCellPopover = ({
  cellData,
  reportingId
}: MonitoringReportCellPopoverProps) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENT_REPORT)

  // Logic for fluids tracking
  const flags = useFeatureFlag({
    flagNames: ['fluid_tracking_monitoring_report']
  })?.data

  const fluidTrackingMonitoringReportEnabled =
    flags?.fluid_tracking_monitoring_report?.enabled

  const hasFluidMonitoring = reportingId === 'fluids_in'

  const getCellTitle = (cellData: MonitoringCellData) => {
    const baseValue = formatData(cellData?.value)

    const secondValue = popoverReportMappings[reportingId as ReportingId]?.title

    const value = `${baseValue} ${secondValue}`

    if (fluidTrackingMonitoringReportEnabled && hasFluidMonitoring) {
      if (!cellData?.target || !cellData) {
        return 'No target set'
      }

      return `Target: ${cellData?.target ? `${formatData(cellData?.target)}ml` : ''}`
    }

    return value
  }

  const getCellDescription = (cellData: MonitoringCellData) =>
    `${popoverReportMappings[reportingId as ReportingId]?.description} ${
      cellData?.date
    } `

  // @ts-ignore
  const logNumber = cellData?.logsCount
  const shouldShowLogNumber = logNumber && logNumber !== cellData?.value

  return (
    <Wrapper>
      <TitleText weight="bold">{cellData && getCellTitle(cellData)}</TitleText>
      <DescriptionText weight="regular">
        {cellData && getCellDescription(cellData)}
      </DescriptionText>
      {shouldShowLogNumber && (
        <FooterText level={2} weight="medium">
          {t(translations.TOTAL_COUNT)}
          {` ${logNumber}`}
        </FooterText>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 224px;
  gap: 10px;
`

const TitleText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const DescriptionText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const FooterText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

export default MonitoringReportCellPopover
